import { parseJwt } from 'helpers/token';

class Location {
  // getter that can be overwritten for test purposes
  getWindow(): Window & typeof globalThis {
    return window;
  }

  getPathName(): string {
    return window.location.pathname.substring(1);
  }

  getReferrer(): string {
    return this.getWindow().document.referrer;
  }

  getReferrerHostname(): string {
    let url;

    try {
      url = new URL(this.getReferrer());
    } catch (e) {
      return '';
    }

    return url.hostname;
  }

  getUrlParam(name: string): string | undefined {
    try {
      return (
        decodeURIComponent(
          (new RegExp(`[?|&]${name}=([^&;]+?)(&|#|;|$)`).exec(window.location.search) || [
            null,
            '',
          ])[1].replace(/\+/g, '%20')
        ) || undefined
      );
    } catch (e) {
      if (this.getWindow().console && console.error) {
        console.error(`Malformed URI param: ${name}`);
      }
      return undefined;
    }
  }

  getHashParam(key: string): string | null {
    const params = window.location.hash.substring(1).split('&');
    const value = params.find((item) => item.split('=')[0] === key);
    return value ? decodeURIComponent(value.split('=')[1]) : null;
  }

  getUtmSearchParams(): Record<string, string> {
    const url = new URL(window.location.href);
    return this.getUtmParams(url.search);
  }

  getUtmHashParams(): Record<string, string> {
    const url = new URL(window.location.href);
    return this.getUtmParams(url.hash.slice(1));
  }

  getUtmParams(paramsString: string): Record<string, string> {
    const params = Array.from(new URLSearchParams(paramsString).entries());

    return params.reduce((acc, [key, value]) => {
      if (key.startsWith('utm_')) {
        return {
          ...acc,
          [key]: value,
        };
      }
      return acc;
    }, {});
  }

  getNthUrlParam(paramIndex: number): string {
    const path = window.location.pathname.substring(1);
    const parts = path.split('/');
    if (paramIndex < parts.length) {
      try {
        return decodeURIComponent(parts[paramIndex]);
      } catch (e) {
        return parts[paramIndex];
      }
    } else {
      return '';
    }
  }

  getLastUrlParam(): string {
    const path = window.location.pathname.substring(1);
    const parts = path.split('/');
    if (parts.length > 0) {
      try {
        return decodeURIComponent(parts[parts.length - 1]);
      } catch (e) {
        return parts[parts.length - 1];
      }
    } else {
      return '';
    }
  }

  getRedirectUri(): string {
    return (
      this.getHashParam('redirect_uri') ||
      this.getUrlParam('redirect_uri') ||
      this.getJWTParam('redirect_uri')
    );
  }

  getState(): string {
    return this.getHashParam('state') || this.getUrlParam('state');
  }

  getJWTParam(param: string): string | null {
    try {
      const jwt = parseJwt(this.getHashParam('state'));
      return jwt[param];
    } catch (e) {
      return null;
    }
  }

  isInLoginButtonFlow(): boolean {
    return (
      /^login-button\//.test(this.getPathName()) ||
      this.getUrlParam('flow') === 'button' ||
      this.getJWTParam('flow') === 'button'
    );
  }

  currentModuleSupportsIframeHiddenFlow(): boolean {
    const pathName = this.getPathName();

    return !/^billing\//.test(pathName);
  }

  isInLoginButtonPopup(): boolean {
    return this.getWindow().opener != null && this.isInLoginButtonFlow();
  }

  isInLoginButtonIframe(): boolean {
    return (
      this.getWindow().top !== this.getWindow().self &&
      this.isInLoginButtonFlow() &&
      this.getRedirectUri() != null &&
      this.currentModuleSupportsIframeHiddenFlow()
    );
  }

  isInIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }

  closePopupWindow(): void {
    this.getWindow().close();
  }

  sendPostMessage(data: Record<string, any>): void {
    const json = { ...data };
    const domain = this.getRedirectUri();

    const state = this.getUrlParam('state') || this.getHashParam('state');
    if (state) {
      json.state = state;
    }

    if (this.isInLoginButtonIframe()) {
      this.getWindow().parent.postMessage(json, domain);
    } else if (this.isInLoginButtonPopup()) {
      this.getWindow().opener.postMessage(json, domain);
      this.closePopupWindow();
    }
  }

  isInLogin = (): boolean => {
    const pathName = this.getPathName();

    return pathName === '' || /^signin/.test(pathName);
  };

  isInLogout = (): boolean => {
    const pathName = this.getPathName();

    return /^logout/.test(pathName);
  };

  isInSignup = (): boolean => {
    const pathName = this.getPathName();

    return /^signup/.test(pathName);
  };

  isTrackingRouteBlocked(): boolean {
    const currentUrl = this.getPathName();
    const blockedRoutes = ['set-password'];

    const isBlocked = !!blockedRoutes.find((route) => new RegExp(`^${route}`).test(currentUrl));

    return isBlocked;
  }

  handleCustomSSORedirect = (): void => {
    const currentUrlParams = new URLSearchParams(window.location.search);
    const searchParamsString = currentUrlParams.toString();
    const newPath = `/custom-sso/${window.location.pathname.split('/').slice(2).join('/')}`;

    // Construct the full URL with parameters
    const redirectUrl = searchParamsString ? `${newPath}?${searchParamsString}` : newPath;

    // Redirect the user to the new URL
    window.location.href = redirectUrl;
  };
}

export default new Location();
